import { Bell, Download } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsProcedures, useConsolideProcedures, useDomainesProcedures, useStructures, useSuiviAgentProcedures, useSuiviProcedure, useSuiviProceduresAgents, useSuiviProceduresParProcedure } from '@/api/hooks';
import { LIBELLE_STATUT_CONSULTATION, STATUTS_CONSULTATION, type FiltreSuiviProcedures, type StatutConsultation } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { StatutConsultationBadge } from '@/components/rh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, dateHeureFr, pct } from '@/lib/utils';
import { CelluleStatutPersonnel } from './Bibliotheque';

type Onglet = 'agents' | 'procedures' | 'consolide';
const message = (e: unknown) => (e instanceof Error ? e.message : 'Action impossible.');
const query = (f: FiltreSuiviProcedures) => {
  const q = new URLSearchParams(Object.entries(f).filter(([, v]) => v !== undefined && v !== '') as Array<[string, string]>).toString();
  return q ? `?${q}` : '';
};

/** Suivi individuel d'un agent : procédures applicables avec statut, première consultation, échéance, confirmation. */
function DialogueAgent({ agentId, onClose }: { agentId: string | null; onClose: () => void }) {
  const { data, isLoading, error } = useSuiviAgentProcedures(agentId ?? undefined);
  return (
    <Dialog open={Boolean(agentId)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{data ? `${data.agent.nom} · ${data.agent.matricule}` : 'Suivi individuel'}</DialogTitle>
          <DialogDescription>{data ? `${data.agent.fonction}${data.agent.structure ? ` · ${data.agent.structure}` : ''} · procédures applicables à la date du jour` : ''}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <Chargement />
        ) : error || !data ? (
          <ErreurBloc erreur={error} />
        ) : !data.procedures.length ? (
          <Vide message="Aucune procédure applicable." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Procédure</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Première consultation</TableHead>
                <TableHead>Échéance</TableHead>
                <TableHead>Confirmation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.procedures.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <Link to={`/procedures/${p.id}`} className="font-medium text-primary hover:underline">
                      {p.reference}
                    </Link>
                    <div className="text-xs">{p.titre}</div>
                  </TableCell>
                  <TableCell className="text-sm">v{p.versionEnVigueur?.numero ?? '—'}</TableCell>
                  <TableCell>
                    <CelluleStatutPersonnel p={p} />
                  </TableCell>
                  <TableCell className="text-sm">{p.personnel?.premiereConsultation ? dateHeureFr(p.personnel.premiereConsultation) : '—'}</TableCell>
                  <TableCell className={cn('text-sm', p.personnel?.enRetard && 'text-red-700')}>{p.personnel?.echeance ? dateFr(p.personnel.echeance) : '—'}</TableCell>
                  <TableCell className="text-sm">{p.confirmationRequise ? (p.personnel?.confirmeeLe ? dateHeureFr(p.personnel.confirmeeLe) : 'attendue') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Listes nominatives d'une procédure, filtrables par état, avec relance ciblée (RH, responsable métier). */
function DialogueProcedure({ procedureId, onClose }: { procedureId: string | null; onClose: () => void }) {
  const { data, isLoading, error } = useSuiviProcedure(procedureId ?? undefined);
  const actions = useActionsProcedures();
  const [etat, setEtat] = useState<'tous' | 'consultes' | 'restants' | 'confirmes'>('tous');
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [texte, setTexte] = useState('');
  const enDefaut = (s: StatutConsultation) => s === 'A_CONSULTER' || s === 'A_RELIRE' || s === 'CONFIRMATION_ATTENDUE';
  const agents = (data?.agents ?? []).filter((a) => (etat === 'tous' ? true : etat === 'consultes' ? !enDefaut(a.statut) || a.statut === 'CONFIRMATION_ATTENDUE' : etat === 'restants' ? a.statut === 'A_CONSULTER' || a.statut === 'A_RELIRE' : a.statut === 'CONFIRMEE'));
  const basculer = (id: string) =>
    setSelection((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const relancer = async () => {
    if (!procedureId) return;
    try {
      const r = await actions.relancer.mutateAsync({ id: procedureId, agentIds: selection.size ? [...selection] : undefined, message: texte.trim() || undefined });
      toast.success(`${r.relances} agent(s) relancé(s).`);
      setSelection(new Set());
      setTexte('');
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open={Boolean(procedureId)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{data ? `${data.procedure.reference} · ${data.procedure.titre}` : 'Agents concernés'}</DialogTitle>
          <DialogDescription>Agents concernés par la version en vigueur{data?.procedure.versionEnVigueur ? ` (v${data.procedure.versionEnVigueur.numero})` : ''} ; cochez des agents pour une relance ciblée, sinon la relance vise tous les agents en défaut.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1">
          {(
            [
              ['tous', 'Tous'],
              ['consultes', 'Ont consulté'],
              ['restants', 'Restent à consulter'],
              ['confirmes', 'Ont confirmé'],
            ] as const
          ).map(([k, l]) => (
            <Button key={k} type="button" size="sm" variant={etat === k ? 'default' : 'outline'} className="h-7" onClick={() => setEtat(k)}>
              {l}
            </Button>
          ))}
        </div>
        {isLoading ? (
          <Chargement />
        ) : error || !data ? (
          <ErreurBloc erreur={error} />
        ) : (
          <div className="max-h-[50vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Structure</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Première consultation</TableHead>
                  <TableHead>Confirmation</TableHead>
                  <TableHead>Rappels</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{enDefaut(a.statut) && <Checkbox checked={selection.has(a.id)} onCheckedChange={() => basculer(a.id)} aria-label={`Sélectionner ${a.nom}`} />}</TableCell>
                    <TableCell>
                      {a.nom}
                      <div className="text-xs text-muted-foreground">{a.matricule}</div>
                    </TableCell>
                    <TableCell className="text-sm">{a.structure ?? '—'}</TableCell>
                    <TableCell>
                      <StatutConsultationBadge statut={a.statut} />
                    </TableCell>
                    <TableCell className="text-sm">{a.premiereConsultation ? dateHeureFr(a.premiereConsultation) : '—'}</TableCell>
                    <TableCell className="text-sm">{a.confirmeeLe ? dateHeureFr(a.confirmeeLe) : data.procedure.confirmationRequise ? 'attendue' : '—'}</TableCell>
                    <TableCell className="text-sm">
                      {a.rappels}
                      {a.dernierRappelLe ? ` · ${dateFr(a.dernierRappelLe)}` : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="relance-texte">Message de relance (facultatif)</Label>
          <Textarea id="relance-texte" rows={2} value={texte} onChange={(e) => setTexte(e.target.value)} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button type="button" onClick={relancer} disabled={actions.relancer.isPending}>
            <Bell className="size-4" /> Relancer {selection.size ? `${selection.size} agent(s)` : 'tous les agents en défaut'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Suivi des consultations (§ 6.4) : par agent, par procédure, consolidé ; filtres, exports Excel, relances. RH ; DG et DGA en synthèse ; directeurs sur option. */
export function SuiviProcedures() {
  const u = useSession()!.utilisateur;
  const [sp] = useSearchParams();
  const synthese = !u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'ADMINISTRATEUR', 'DIRECTEUR'].includes(p)) && u.profils.some((p) => ['DIRECTEUR_GENERAL', 'DGA_OPERATIONS', 'DGA_FINANCES'].includes(p));
  const [onglet, setOnglet] = useState<Onglet>(synthese ? 'consolide' : sp.get('procedureId') ? 'procedures' : 'agents');
  const [f, setF] = useState<FiltreSuiviProcedures>({ procedureId: sp.get('procedureId') ?? undefined });
  const structures = useStructures();
  const domaines = useDomainesProcedures();
  const agents = useSuiviProceduresAgents(f, onglet === 'agents');
  const procedures = useSuiviProceduresParProcedure(f, onglet === 'procedures');
  const consolide = useConsolideProcedures(f, onglet === 'consolide');
  const [agentOuvert, setAgentOuvert] = useState<string | null>(null);
  const [procedureOuverte, setProcedureOuverte] = useState<string | null>(null);
  const maj = (k: keyof FiltreSuiviProcedures, v: string) => setF((x) => ({ ...x, [k]: v || undefined }));
  const exportRh = u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'ADMINISTRATEUR', 'DIRECTEUR'].includes(p));
  return (
    <div className="space-y-4">
      <PageHeader
        titre="Suivi des consultations des procédures"
        sousTitre="Indicateurs établis sur les agents effectivement concernés (affectations du jour) et sur la version en vigueur ; un agent non concerné n'est jamais en défaut. Le statut « Lu » est une trace d'affichage dans le lecteur, non une preuve de lecture intégrale."
        actions={exportRh && !synthese ? <BoutonExcel chemin={`/procedures/suivi/export.xlsx${query(f)}`} nom="FONAREV360_Suivi_procedures.xlsx" libelle="Exporter (Excel)" /> : undefined}
      />
      <Card>
        <CardContent className="grid gap-3 pt-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-1">
            <Label htmlFor="s-structure">Direction</Label>
            <SelectNatif id="s-structure" value={f.structure ?? ''} onChange={(e) => maj('structure', e.target.value)}>
              <option value="">Toutes</option>
              {(structures.data ?? []).map((s) => (
                <option key={s.id} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-domaine">Domaine</Label>
            <SelectNatif id="s-domaine" value={f.domaineId ?? ''} onChange={(e) => maj('domaineId', e.target.value)}>
              <option value="">Tous</option>
              {(domaines.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-statut">Statut</Label>
            <SelectNatif id="s-statut" value={f.statut ?? ''} onChange={(e) => maj('statut', e.target.value)}>
              <option value="">Tous</option>
              {STATUTS_CONSULTATION.map((s) => (
                <option key={s} value={s}>
                  {LIBELLE_STATUT_CONSULTATION[s]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-depuis">Consultations depuis le</Label>
            <Input id="s-depuis" type="date" value={f.depuis ?? ''} onChange={(e) => maj('depuis', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="s-jusqua">Jusqu'au</Label>
            <Input id="s-jusqua" type="date" value={f.jusqua ?? ''} onChange={(e) => maj('jusqua', e.target.value)} />
          </div>
        </CardContent>
      </Card>
      <Tabs value={onglet} onValueChange={(v) => setOnglet(v as Onglet)}>
        <TabsList>
          {!synthese && <TabsTrigger value="agents">Par agent</TabsTrigger>}
          {!synthese && <TabsTrigger value="procedures">Par procédure</TabsTrigger>}
          <TabsTrigger value="consolide">Consolidé</TabsTrigger>
        </TabsList>
      </Tabs>

      {onglet === 'agents' && (
        <Card data-testid="suivi-agents">
          <CardHeader>
            <CardTitle>Suivi par agent</CardTitle>
            <CardDescription>Chaque ligne ouvre le détail des procédures applicables à l'agent.</CardDescription>
          </CardHeader>
          <CardContent>
            {agents.isLoading ? (
              <Chargement />
            ) : agents.error ? (
              <ErreurBloc erreur={agents.error} />
            ) : !agents.data?.length ? (
              <Vide message="Aucun agent dans ce périmètre." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Direction</TableHead>
                    <TableHead className="text-right">Applicables</TableHead>
                    <TableHead className="text-right">Lues</TableHead>
                    <TableHead className="text-right">À consulter</TableHead>
                    <TableHead className="text-right">À relire</TableHead>
                    <TableHead className="text-right">Confirmations attendues</TableHead>
                    <TableHead className="text-right">En retard</TableHead>
                    <TableHead>Dernière consultation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.data.map((l) => (
                    <TableRow key={l.agent.id} className="cursor-pointer" onClick={() => setAgentOuvert(l.agent.id)}>
                      <TableCell>
                        <span className="font-medium text-primary">{l.agent.nom}</span>
                        <div className="text-xs text-muted-foreground">
                          {l.agent.matricule} · {l.agent.fonction}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{l.structure?.code ?? '—'}</TableCell>
                      <TableCell className="num text-right">{l.applicables}</TableCell>
                      <TableCell className="num text-right">{l.lues}</TableCell>
                      <TableCell className={cn('num text-right', l.aConsulter > 0 && 'text-amber-700')}>{l.aConsulter}</TableCell>
                      <TableCell className={cn('num text-right', l.aRelire > 0 && 'text-amber-700')}>{l.aRelire}</TableCell>
                      <TableCell className={cn('num text-right', l.confirmationsAttendues > 0 && 'text-amber-700')}>{l.confirmationsAttendues}</TableCell>
                      <TableCell className={cn('num text-right', l.enRetard > 0 && 'font-medium text-red-700')}>{l.enRetard}</TableCell>
                      <TableCell className="text-sm">{l.derniereConsultation ? dateHeureFr(l.derniereConsultation) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {onglet === 'procedures' && (
        <Card data-testid="suivi-procedures">
          <CardHeader>
            <CardTitle>Suivi par procédure</CardTitle>
            <CardDescription>Chaque ligne ouvre les listes nominatives (concernés, ayant consulté, restant à consulter, confirmations) et la relance ciblée.</CardDescription>
          </CardHeader>
          <CardContent>
            {procedures.isLoading ? (
              <Chargement />
            ) : procedures.error ? (
              <ErreurBloc erreur={procedures.error} />
            ) : !procedures.data?.length ? (
              <Vide message="Aucune procédure publiée dans ce périmètre." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Procédure</TableHead>
                    <TableHead>Version · échéance</TableHead>
                    <TableHead className="text-right">Concernés</TableHead>
                    <TableHead className="text-right">Ont consulté</TableHead>
                    <TableHead className="text-right">Restent</TableHead>
                    <TableHead className="text-right">Taux</TableHead>
                    <TableHead className="text-right">Confirmations</TableHead>
                    <TableHead className="text-right">En retard</TableHead>
                    <TableHead className="text-right">Relances</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {procedures.data.map((l) => (
                    <TableRow key={l.procedure.id} className="cursor-pointer" onClick={() => setProcedureOuverte(l.procedure.id)}>
                      <TableCell>
                        <span className="font-medium text-primary">{l.procedure.reference}</span>
                        <div className="text-xs">{l.procedure.titre}</div>
                        <div className="text-xs text-muted-foreground">{l.procedure.domaine.libelle}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        v{l.procedure.versionEnVigueur?.numero ?? '—'}
                        {l.echeance ? ` · ${dateFr(l.echeance)}` : ''}
                      </TableCell>
                      <TableCell className="num text-right">{l.concernes}</TableCell>
                      <TableCell className="num text-right">{l.consultes}</TableCell>
                      <TableCell className={cn('num text-right', l.restants > 0 && 'text-amber-700')}>{l.restants}</TableCell>
                      <TableCell className="num text-right">{pct(l.taux)}</TableCell>
                      <TableCell className="num text-right">{l.confirmationsRequises ? `${l.confirmationsRecues} / ${l.confirmationsRecues + l.confirmationsAttendues}` : '—'}</TableCell>
                      <TableCell className={cn('num text-right', l.enRetard > 0 && 'font-medium text-red-700')}>{l.enRetard}</TableCell>
                      <TableCell className="num text-right">{l.relances}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {onglet === 'consolide' &&
        (consolide.isLoading ? (
          <Chargement />
        ) : consolide.error || !consolide.data ? (
          <ErreurBloc erreur={consolide.error} />
        ) : (
          <div className="space-y-4" data-testid="suivi-consolide">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <Kpi label="Procédures publiées" valeur={consolide.data.publiees} />
              <Kpi label="Affectations" valeur={consolide.data.concernes} detail="agents × procédures applicables" />
              <Kpi label="Taux de consultation" valeur={pct(consolide.data.tauxGlobal)} detail={`${consolide.data.consultes} consultation(s)`} accent={consolide.data.tauxGlobal !== null && consolide.data.tauxGlobal < 50 ? 'alerte' : 'ok'} />
              <Kpi label="Confirmations attendues" valeur={consolide.data.confirmationsAttendues} accent={consolide.data.confirmationsAttendues > 0 ? 'alerte' : undefined} />
              <Kpi label="En retard" valeur={consolide.data.enRetard} detail="échéance dépassée" accent={consolide.data.enRetard > 0 ? 'critique' : 'ok'} />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Taux de consultation par direction</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Direction</TableHead>
                        <TableHead className="text-right">Agents</TableHead>
                        <TableHead className="text-right">Affectations</TableHead>
                        <TableHead className="text-right">Consultées</TableHead>
                        <TableHead className="text-right">Taux</TableHead>
                        <TableHead className="text-right">En retard</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consolide.data.parDirection.map((d) => (
                        <TableRow key={d.code}>
                          <TableCell>
                            <span className="font-medium">{d.code}</span> <span className="text-xs text-muted-foreground">{d.libelle}</span>
                          </TableCell>
                          <TableCell className="num text-right">{d.agents}</TableCell>
                          <TableCell className="num text-right">{d.affectations}</TableCell>
                          <TableCell className="num text-right">{d.consultees}</TableCell>
                          <TableCell className="num text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="h-2 w-16 overflow-hidden rounded bg-muted">
                                <div className="h-2 bg-primary" style={{ width: `${d.taux ?? 0}%` }} />
                              </div>
                              {pct(d.taux)}
                            </div>
                          </TableCell>
                          <TableCell className={cn('num text-right', d.enRetard > 0 && 'text-red-700')}>{d.enRetard}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Procédures les moins consultées</CardTitle>
                </CardHeader>
                <CardContent>
                  {!consolide.data.moinsConsultees.length ? (
                    <Vide message="Aucune procédure concernant des agents du périmètre." />
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Procédure</TableHead>
                          <TableHead className="text-right">Concernés</TableHead>
                          <TableHead className="text-right">Consultée</TableHead>
                          <TableHead className="text-right">Taux</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {consolide.data.moinsConsultees.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>
                              <Link to={`/procedures/${p.id}`} className="font-medium text-primary hover:underline">
                                {p.reference}
                              </Link>
                              <div className="text-xs">{p.titre}</div>
                            </TableCell>
                            <TableCell className="num text-right">{p.concernes}</TableCell>
                            <TableCell className="num text-right">{p.consultes}</TableCell>
                            <TableCell className="num text-right">{pct(p.taux)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Nouvelles publications (30 jours)</CardTitle>
                </CardHeader>
                <CardContent>
                  {!consolide.data.nouvellesPublications.length ? (
                    <Vide message="Aucune publication récente." />
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {consolide.data.nouvellesPublications.map((p) => (
                        <li key={p.id} className="flex flex-wrap items-center gap-2">
                          <Link to={`/procedures/${p.id}`} className="font-medium text-primary hover:underline">
                            {p.reference}
                          </Link>
                          <span>{p.titre}</span>
                          <Badge variant="outline">v{p.version}</Badge>
                          <span className="text-xs text-muted-foreground">
                            publiée le {dateFr(p.datePublication)}
                            {p.echeance ? ` · échéance ${dateFr(p.echeance)}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Évolution des consultations</CardTitle>
                  <CardDescription>Premières consultations et confirmations par mois, six derniers mois.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Mois</TableHead>
                        <TableHead className="text-right">Consultations</TableHead>
                        <TableHead className="text-right">Confirmations</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consolide.data.evolution.map((e) => {
                        const max = Math.max(1, ...consolide.data!.evolution.map((x) => x.consultations));
                        return (
                          <TableRow key={e.mois}>
                            <TableCell>{e.mois}</TableCell>
                            <TableCell className="num text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="h-2 w-24 overflow-hidden rounded bg-muted">
                                  <div className="h-2 bg-primary" style={{ width: `${(e.consultations / max) * 100}%` }} />
                                </div>
                                {e.consultations}
                              </div>
                            </TableCell>
                            <TableCell className="num text-right">{e.confirmations}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
            {exportRh && !synthese && (
              <div className="flex justify-end">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/procedures/gestion">
                    <Download className="size-4" /> Gestion des procédures
                  </Link>
                </Button>
              </div>
            )}
          </div>
        ))}
      <DialogueAgent agentId={agentOuvert} onClose={() => setAgentOuvert(null)} />
      <DialogueProcedure procedureId={procedureOuverte} onClose={() => setProcedureOuverte(null)} />
    </div>
  );
}
